/**
 * Korvattu rxfire/firestore-kirjasto paikallisella toteutuksella,
 * koska rxfire ei toimi uusimman Angularin kanssa.
 *
 * Alkuperäinen lähde: https://github.com/FirebaseExtended/rxfire
 * Lisenssi: Apache-2.0
 */

import {
  onSnapshot,
  DocumentReference,
  DocumentData,
  SnapshotListenOptions,
  Query,
  DocumentSnapshot,
  QuerySnapshot,
  QueryDocumentSnapshot,
  DocumentChange,
  DocumentChangeType,
  refEqual,
} from 'firebase/firestore'
import { Observable, MonoTypeOperatorFunction, OperatorFunction, pipe, UnaryFunction } from 'rxjs'
import { map, filter, startWith, pairwise } from 'rxjs/operators'

const DEFAULT_OPTIONS: SnapshotListenOptions = { includeMetadataChanges: false }

export function fromRef<T = DocumentData>(ref: DocumentReference<T>, options?: SnapshotListenOptions): Observable<DocumentSnapshot<T>>
export function fromRef<T = DocumentData>(ref: Query<T>, options?: SnapshotListenOptions): Observable<QuerySnapshot<T>>
export function fromRef(ref: any, options: SnapshotListenOptions = DEFAULT_OPTIONS): Observable<any> {
  return new Observable(subscriber => {
    const unsubscribe = onSnapshot(ref, options, {
      next: subscriber.next.bind(subscriber),
      error: subscriber.error.bind(subscriber),
      complete: subscriber.complete.bind(subscriber),
    })
    return { unsubscribe }
  })
}

export function doc<T = DocumentData>(ref: DocumentReference<T>): Observable<DocumentSnapshot<T>> {
  return fromRef(ref, { includeMetadataChanges: true })
}

export function collection<T = DocumentData>(query: Query<T>): Observable<QueryDocumentSnapshot<T>[]> {
  return fromRef(query, { includeMetadataChanges: true }).pipe(map(changes => changes.docs))
}

const ALL_EVENTS: DocumentChangeType[] = ['added', 'modified', 'removed']

const filterEvents = <T>(events?: DocumentChangeType[]): MonoTypeOperatorFunction<DocumentChange<T>[]> =>
  filter((changes: DocumentChange<T>[]) => {
    for (let i = 0; i < changes.length; i++) {
      if (events && events.indexOf(changes[i].type) >= 0) {
        return true
      }
    }
    return false
  })

const windowwise = <T = unknown>() => pipe(startWith(undefined), pairwise() as OperatorFunction<T | undefined, [T | undefined, T]>)

const metaDataEquals = <T, R extends QuerySnapshot<T> | QueryDocumentSnapshot<T>>(a: R, b: R) => JSON.stringify(a.metadata) === JSON.stringify(b.metadata)

const filterEmptyUnlessFirst = <T = unknown>(): UnaryFunction<Observable<T[]>, Observable<T[]>> =>
  pipe(
    windowwise(),
    filter(([prior, current]) => current.length > 0 || prior === undefined),
    map(([, current]) => current),
  )

export function collectionChanges<T = DocumentData>(query: Query<T>, options: { events?: DocumentChangeType[] } = {}): Observable<DocumentChange<T>[]> {
  return fromRef(query, { includeMetadataChanges: true }).pipe(
    windowwise(),
    map(([priorSnapshot, currentSnapshot]) => {
      const docChanges = currentSnapshot.docChanges()
      if (priorSnapshot && !metaDataEquals(priorSnapshot, currentSnapshot)) {
        currentSnapshot.docs.forEach((currentDocSnapshot, currentIndex) => {
          const currentDocChange = docChanges.find(c => refEqual(c.doc.ref, currentDocSnapshot.ref))
          if (currentDocChange) {
            if (metaDataEquals(currentDocChange.doc, currentDocSnapshot)) {
              return
            }
          } else {
            const priorDocSnapshot = priorSnapshot?.docs.find(d => refEqual(d.ref, currentDocSnapshot.ref))
            if (priorDocSnapshot && metaDataEquals(priorDocSnapshot, currentDocSnapshot)) {
              return
            }
          }
          docChanges.push({
            oldIndex: currentIndex,
            newIndex: currentIndex,
            type: 'modified',
            doc: currentDocSnapshot,
          })
        })
      }
      return docChanges
    }),
    filterEvents(options.events || ALL_EVENTS),
    filterEmptyUnlessFirst(),
  )
}
